import {DispatchRequest, DispatchRequestEntity} from "../../../model/dispatch-request";
import {createSelector} from "@reduxjs/toolkit";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {TransportCategory} from "../../../model/transport-category";

export interface DispatchRequestSelectorProps {
  regionId?: string;
  category?: TransportCategory
  pickupSiteId?: string;
  deliverySiteId?: string;
  freightTypeId?: string
}

const getRegionIdParam = createParamSelector<DispatchRequestSelectorProps | undefined, string | undefined>(params => params?.regionId);
const getCategoryParam = createParamSelector<DispatchRequestSelectorProps | undefined, TransportCategory | undefined>(params => params?.category);
const getPickupSiteIdParam = createParamSelector<DispatchRequestSelectorProps | undefined, string | undefined>(params => params?.pickupSiteId);
const getDeliverySiteIdParam = createParamSelector<DispatchRequestSelectorProps | undefined, string | undefined>(params => params?.deliverySiteId);
const getFreightTypeIdParam = createParamSelector<DispatchRequestSelectorProps | undefined, string | undefined>(params => params?.freightTypeId);

export const dispatchRequestsSelector = () => createSelector(
  [
    getUseQueryResultData<DispatchRequest[]>,
    getRegionIdParam,
    getCategoryParam,
    getPickupSiteIdParam,
    getDeliverySiteIdParam,
    getFreightTypeIdParam,
  ],
  (
    data,
    regionId,
    category,
    pickupSiteId,
    deliverySiteId,
    freightTypeId,
  ) => {
    return selectDispatchRequestsAndTransform(
      data ?? [], {
        regionId: regionId,
        category: category,
        pickupSiteId: pickupSiteId,
        deliverySiteId: deliverySiteId,
        freightTypeId: freightTypeId,
      }
    );
  }
);

export const transformDispatchRequest = (value: DispatchRequest): DispatchRequestEntity => {
  return DispatchRequestEntity.from(value);
};

export const selectDispatchRequests = (values?: DispatchRequest[], selector?: DispatchRequestSelectorProps): DispatchRequest[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;

    if (selector?.regionId) {
      if (selector.regionId !== v.regionId) {
        filterIn = false;
      }
    }

    if (selector?.category) {
      if (selector.category.code !== v.category) {
        filterIn = false;
      }
    }

    if (selector?.pickupSiteId) {
      if (selector.pickupSiteId !== v.pickupSiteId) {
        filterIn = false;
      }
    }

    if (selector?.deliverySiteId) {
      if (selector.deliverySiteId !== v.deliverySiteId) {
        filterIn = false;
      }
    }

    if (selector?.freightTypeId) {
      if (selector.freightTypeId !== v.freightTypeId) {
        filterIn = false;
      }
    }

    return filterIn;
  });
};

export const selectDispatchRequestsAndTransform = (values?: DispatchRequest[], selector?: DispatchRequestSelectorProps): DispatchRequestEntity[] => {
  const selected = selectDispatchRequests(values, selector);
  return selected.map(transformDispatchRequest);
};
